import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
    checkIsCurrentUser,
    selectUser,
    selectUserAddress,
    selectUserCompletion,
    selectUserFullName,
    selectUserPhones,
    selectUserProfileImage,
} from "../../../slices/user-slice";
import LocationIconSVG from "../../resources/svg/LocationIconSVG";
import HeartIconSVG from "../../resources/svg/HeartIconSVG";
import EditPencilIconSVG from "../../resources/svg/EditPencilIconSVG";
import SubscribeIconSVG from "../../resources/svg/SubscribeIconSVG";
import useScreenSize from "../../../custom-hooks/base-hooks/useScreenSize";
import EditProfile from "./edit-profile/EditProfileForm";
import InformationalIconSVG from "../../resources/svg/InformationalIconSVG";
import SharePopup from "../../resources/components/popups/SharePopup";
import UserProfileContactsCard from "./UserProfileContactsCard";
import { Link, useNavigate, useParams } from "react-router-dom";
import { WorkCollectionMeta } from "../../../domain/models/dto/WorkDTO";
import { useAppDispatch } from "../../../custom-hooks/useAppDispatch";
import { UserServiceImpl } from "../../../services/UserServiceImpl";
import BlockButton from "../../resources/components/buttons/BlockButton";
import BlockedUsersListPopup from "./BlockedUsersListPopup";
import { clientURL } from "../../../Config";

interface UserProfileDescriptionProps {
    worksMeta: WorkCollectionMeta;
}

const UserProfileDescription: FC<UserProfileDescriptionProps> = (props) => {
    const [isEditProfileVisible, setIsEditProfileVisible] = useState(false);

    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { clientId } = useParams();

    const user = useSelector(selectUser(clientId));
    const isCurrentUser = useSelector(checkIsCurrentUser(clientId));
    const showCurrentUserContent = !clientId || isCurrentUser;
    const fullName = useSelector(selectUserFullName(clientId));
    const address = useSelector(selectUserAddress(clientId));
    const completed = useSelector(selectUserCompletion(clientId));
    const profileImage = useSelector(selectUserProfileImage(clientId));
    const phones = useSelector(selectUserPhones(clientId));
    const { upTo650 } = useScreenSize();
    const navigate = useNavigate();
    const viewedProfileId = clientId || user.id;
    const publicProfileUrl = `${clientURL.replace(/\/$/, "")}/profile/${encodeURIComponent(viewedProfileId)}`;

    useEffect(() => {
        if (!clientId) return;
        (async () => dispatch(UserServiceImpl.getUserByIdThunk(clientId)))();
    }, [clientId]);

    return (
        <div className="flex dmax5:flex-col justify-center items-start tmax4:items-center mb-[60px]">
            <div className="relative min-w-[483px] max-w-[483px] dmax2:min-w-[360px] dmax2:max-w-[360px] h-[483px] dmax2:h-[360px] di6:mr-5 dmin2:mr-[60px] td5:mb-6 tmax5:mb-4">
                <img src={profileImage} alt="user-photo" className="object-cover rounded-xl w-full h-full" />
            </div>
            <div className="relative flex flex-col w-full w-max-[737px]">
                <div className="relative flex tmax4:flex-col justify-between tmax4:items-start mb-7 w-full">
                    <div className="relative flex flex-col w-full">
                        {upTo650 && showCurrentUserContent && (
                            <div className="flex justify-center mt-4 mb-6 w-[354px] mx-auto">
                                <div
                                    onClick={() => setIsEditProfileVisible(true)}
                                    className="button dmin2:H14B td2:H14 tmax1:H12 w-[52px] dmin2:h-[50px] td2:h-[40px] tmax1:h-[37px] mr-4 px-4"
                                >
                                    <EditPencilIconSVG className="w-5" />
                                </div>
                                <div
                                    onClick={() => navigate("/saved")}
                                    className="button dmin2:H14B td2:H14 tmax1:H12 w-[142px] dmin2:h-[50px] td2:h-[40px] tmax1:h-[37px] mr-4 px-4"
                                >
                                    <HeartIconSVG className="w-5 mr-[10px] text-textWhite" strokeWidth="3" />
                                    <div className="mb-[1px]">{t("common.saved")}</div>
                                </div>
                                <Link
                                    to="/subscription"
                                    className="button dmin2:H14B td2:H14 tmax1:H12 w-[142px] dmin2:h-[50px] td2:h-[40px] tmax1:h-[37px] px-4"
                                >
                                    <SubscribeIconSVG className="w-5 mr-[10px] text-textWhite" strokeWidth="3" />
                                    <div className="mb-[1px]">{t("common.subscription")}</div>
                                </Link>
                            </div>
                        )}
                        <div className="flex items-center">
                            <p className="H32B dmax2:H24B text-textWhite mr-5 dmax2:mr-3 max-w-[550px] td4:max-w-[420px] tmax4:max-w-[320px] break-words">
                                {fullName}
                            </p>
                            {/* <div className="flex items-center">
                                <span className="H24B dmax2:H16 text-textWhite mr-1 dmax2:mr-[2px]">5,0</span>
                                <RatingStarSVG className="w-8 dmax2:w-5 mr-2 dmax2:mr-1" />
                                <span className="H24B dmax2:H16 text-textFaded underline underline-offset-8 dmax2:underline-offset-4">
                                    (12)
                                </span>
                            </div> */}
                        </div>
                        {address && (
                            <div className="flex items-center mt-5 dmax2:mt-2">
                                <p className="H24 dmax2:H16 text-textFaded mr-[18px] max-w-[550px] td4:max-w-[420px] tmax4:max-w-[300px] break-words">
                                    {address}
                                </p>
                                <LocationIconSVG className="min-w-8 dmax2:min-w-6 text-textFaded mr-6" />
                            </div>
                        )}
                    </div>
                    <div className="flex tmin4:flex-col items-end tmax4:mt-6 gap-2">
                        <SharePopup shareUrl={publicProfileUrl} shareText={fullName} />
                        {showCurrentUserContent ? (
                            <BlockedUsersListPopup />
                        ) : (
                            <BlockButton userId={clientId} isBlocked={user.isBlocked} />
                        )}
                    </div>
                </div>
                {user.about && (
                    <div className="max-w-[737px] di7:max-w-[582px] break-words">
                        <p className="H24B text-textWhite mb-4">{t("userProfile.aboutMe")}</p>
                        <p className="H16 text-textFaded mb-7">{user.about}</p>
                    </div>
                )}
                <div className="mb-7 overflow-x-auto no-scrollbar">
                    <div className="flex min-w-[510px]">
                        <div className="bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] px-9 dmax2:px-4 pb-[9.5px] dmax2:pb-3 pt-[10.5px] dmax2:pt-[11px] rounded-[30px] cursor-pointer mr-4 transition-all duration-300">
                            <p className="H14 dmax2:H12 text-textWhite">
                                {t("userProfile.completedOrders")} {props.worksMeta.completedWorksCount}
                            </p>
                        </div>
                        <div className="bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] px-9 dmax2:px-4 pb-[9.5px] dmax2:pb-3 pt-[10.5px] dmax2:pt-[11px] rounded-[30px] cursor-pointer mr-4 transition-all duration-300">
                            <p className="H14 dmax2:H12 text-textWhite">
                                {t("userProfile.createdOrders")} {props.worksMeta.createdWorksCount}
                            </p>
                        </div>
                        {showCurrentUserContent && (
                            <div className="bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] px-9 dmax2:px-4 pb-[9.5px] dmax2:pb-3 pt-[10.5px] dmax2:pt-[11px] rounded-[30px] cursor-pointer transition-all duration-300">
                                <p className="H14 dmax2:H12 text-textWhite">{t("userProfile.myOrders")} 0</p>
                            </div>
                        )}
                    </div>
                </div>
                {!completed && upTo650 && (
                    <div className="flex items-center dmax2:items-start dmin2:mt-7 dmax2:mb-6">
                        <InformationalIconSVG className="min-w-8 w-8 dmax2:min-w-6 dmax2:w-6 h-8 dmax2:h-6 text-textWarning mr-3" />
                        <span className="H20 dmax2:H16 text-textWarning">{t("errors.profileIncomplete")}</span>
                    </div>
                )}
                <UserProfileContactsCard phones={phones} telegram={user.telegram} />
                {!upTo650 && showCurrentUserContent && (
                    <div className="flex mt-10">
                        <div
                            onClick={() => navigate("/saved")}
                            className="button dmin2:H16 td2:H14 tmax1:H12 dmin2:h-[50px] td2:h-[40px] tmax1:h-[37px] mr-4 px-6 transition-all duration-300"
                        >
                            <HeartIconSVG className="w-6 mr-[10px] text-textWhite" strokeWidth="3" />
                            <div className="mb-[2px]">{t("common.saved")}</div>
                        </div>
                        <div
                            onClick={() => setIsEditProfileVisible(true)}
                            className="button dmin2:H16 td2:H14 tmax1:H12 dmin2:h-[50px] td2:h-[40px] tmax1:h-[37px] mr-4 px-6 transition-all duration-300"
                        >
                            <EditPencilIconSVG className="w-6 mr-[10px] text-textWhite" />
                            <div className="mb-[2px]">{t("userProfile.editProfile.editProfileAction")}</div>
                        </div>
                        <Link
                            to="/subscription"
                            className="button dmin2:H16 td2:H14 tmax1:H12 dmin2:h-[50px] td2:h-[40px] tmax1:h-[37px] px-6 transition-all duration-300"
                        >
                            <SubscribeIconSVG className="w-6 mr-[10px] text-textWhite" strokeWidth="3" />
                            <div className="mb-[2px]">{t("common.subscription")}</div>
                        </Link>
                    </div>
                )}
                {!completed && !upTo650 && showCurrentUserContent && (
                    <div className="flex items-center dmax2:items-start mt-7 dmax2:mt-6">
                        <InformationalIconSVG className="min-w-8 w-8 dmax2:min-w-6 dmax2:w-6 h-8 dmax2:h-6 text-textWarning mr-3" />
                        <span className="H20 dmax2:H16 text-textWarning">{t("errors.profileIncomplete")}</span>
                    </div>
                )}
                <EditProfile isVisible={isEditProfileVisible} setIsVisible={setIsEditProfileVisible} />
            </div>
        </div>
    );
};

export default UserProfileDescription;
